import { extendTheme } from '@chakra-ui/react';
import { BR } from './theme/brand';

interface ThemeConfig {
  initialColorMode: 'light' | 'dark';
  useSystemColorMode: boolean;
}

const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false,
};

/** Always use light mode — ignore stored/system preference */
export const lightModeManager = {
  get() {
    return 'light' as const;
  },
  set() {},
  type: 'localStorage' as const,
};

const colors = {
  // Button color - #FF6544
  primary: {
    50: "#fff5f3",
    100: "#ffe8e3",
    200: "#ffd4cc",
    300: "#ffb5a8",
    400: "#ff8a75",
    500: "#FF6544", // Primary button color
    600: "#e8592f",
    700: "#c04722",
    800: "#9f3a1c",
    900: "#83321b",
  },
  // Main theme colors - #302E3F and #333142
  dark: {
    50: "#f7f7f8",
    100: "#ededef",
    200: "#d8d8dc",
    300: "#b8b8bf",
    400: "#92929d",
    500: "#757582",
    600: "#5f5f6a",
    700: "#4e4e56",
    800: "#333142", // Secondary dark color
    900: "#302E3F", // Primary dark color
  },
  // Light background - #FEF9F1
  light: {
    50: "#FEF9F1", // Primary light color
    100: "#fdf4e6",
    200: "#fae7c7",
    300: "#f6d69e",
    400: "#f0c071",
    500: "#e9a850",
    600: "#d9903a",
    700: "#b5762f",
    800: "#925f2d",
    900: "#774f28",
  },
  // Keep neutral colors for general use
  neutral: {
    50: "#fafafa",
    100: "#f5f5f5",
    200: "#e5e5e5",
    300: "#d4d4d4",
    400: "#a3a3a3",
    500: "#737373", 
    600: "#525252",
    700: "#404040",
    800: "#262626",
    900: "#171717",
  },
};

const fonts = {
  heading: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
  body: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
};

const fontWeights = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
};

const shadows = {
  subtle: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  soft: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  medium: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  elevated: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  prominent: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
};

const styles = {
  global: {
    body: {
      bg: BR.bgAlt,
      color: BR.text,
    },
    html: {
      bg: BR.bgAlt,
    },
  },
};

const components = {
  Button: {
    baseStyle: {
      fontWeight: "semibold",
      borderRadius: "md",
    },
    variants: {
      solid: {
        bg: "primary.500",
        color: "white",
        _hover: {
          bg: "primary.600",
          transform: "translateY(-2px)",
          boxShadow: "lg",
        },
        _active: {
          bg: "primary.700",
        },
      },
      outline: {
        borderWidth: "2px",
        borderColor: "primary.500",
        color: "primary.500",
        _hover: {
          bg: "primary.50",
          transform: "translateY(-2px)",
        },
      },
      accent: {
        bg: BR.purple,
        color: "white",
        _hover: {
          bg: BR.purpleDark,
          transform: "translateY(-2px)",
          boxShadow: "lg",
        },
      },
    },
  },
  Heading: {
    baseStyle: {
      fontWeight: "bold",
      lineHeight: "1.2",
      color: BR.text,
    },
  },
  Text: {
    baseStyle: {
      color: BR.text,
      fontSize: "md",
    },
  },
  Skeleton: {
    baseStyle: {
      startColor: "rgba(107, 45, 140, 0.06)",
      endColor: "rgba(107, 45, 140, 0.12)",
    },
  },
};

export const theme = extendTheme({
  config,
  colors,
  fonts,
  fontWeights,
  shadows,
  styles,
  components,
});